import { PageHeaderLink } from "@/components/PageHeaderLink";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldSet,
} from "@/components/ui/field";
import { useForm } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

async function createService(
	appId: string,
	body: { name: string; url: string; endpoint: string },
): Promise<{ id: string }> {
	const res = await fetch(`/api/applications/${appId}/services`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(body),
	});
	const data = (await res.json().catch(() => null)) as {
		id?: string;
		error?: string;
	} | null;
	if (!res.ok) throw new Error(data?.error ?? "Failed to create service");
	if (!data?.id) throw new Error("Failed to create service");
	return { id: data.id };
}

const schema = z.object({
	name: z
		.string("Name is required")
		.min(1, "Name is required")
		.max(255, "Name is too long"),
	url: z.url("Base URL is invalid"),
	endpoint: z
		.string("Endpoint path is required")
		.min(1, "Endpoint path is required"),
});

export function NewService() {
	const { appId } = useParams<{ appId: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (body: { name: string; url: string; endpoint: string }) =>
			createService(appId ?? "", body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["services", appId] });
		},
	});

	const form = useForm<z.infer<typeof schema>>({
		validators: {
			onSubmit: schema,
		},
		defaultValues: { name: "", url: "", endpoint: "/" },
		onSubmit: async ({ value }) => {
			const data = await mutation.mutateAsync({
				name: value.name.trim(),
				url: value.url.trim(),
				endpoint: value.endpoint || "/",
			});
			navigate(`/${appId}/${data.id}`, { replace: true });
		},
	});

	return (
		<Shell className="flex flex-col gap-4">
			<PageHeaderLink to={appId ? `/${appId}` : "/"} label="Services" />
			<Card>
				<CardHeader>
					<CardTitle>New service</CardTitle>
					<CardDescription>
						Add an endpoint to monitor (HTTP GET).
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					{mutation.error && (
						<p
							className="text-sm text-destructive bg-destructive/10 p-2 rounded"
							role="alert"
						>
							{mutation.error.message}
						</p>
					)}
					<form
						id="new-service"
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
					>
						<FieldGroup>
							<FieldSet>
								<form.Field name="name">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="name">Name</FieldLabel>
											<Input
												id="name"
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
												onBlur={field.handleBlur}
												placeholder="API"
												required
												disabled={mutation.isPending}
											/>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
								<form.Field name="url">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="url">Base URL</FieldLabel>
											<Input
												id="url"
												type="url"
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
												onBlur={field.handleBlur}
												placeholder="https://api.example.com"
												required
												disabled={mutation.isPending}
											/>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
								<form.Field name="endpoint">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="endpoint">Endpoint path</FieldLabel>
											<Input
												id="endpoint"
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
												onBlur={field.handleBlur}
												placeholder="/"
												disabled={mutation.isPending}
											/>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
							</FieldSet>
						</FieldGroup>
					</form>
				</CardContent>
				<CardFooter className="grid">
					<form.Subscribe
						selector={(state) => [
							state.isSubmitting,
							state.isValid,
							state.isDirty,
						]}
					>
						{([isSubmitting, isValid, isDirty]) => (
							<Button
								type="submit"
								form="new-service"
								disabled={!isValid || !isDirty}
								loading={isSubmitting || mutation.isPending}
							>
								Create
							</Button>
						)}
					</form.Subscribe>
				</CardFooter>
			</Card>
		</Shell>
	);
}
